import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import food_menu_bg from "../../../assets/images/backgrounds/food-menu.png";
import { getStoreInfo } from "../../../utils/utils";
import { showToast } from "../../../store/reducers/toasterReducer";

const Menus = ({ menuItems = [], categories = [], hasBanner = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const menuSectionRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const scannedTable =
    sessionStorage.getItem("scannedTableId") ||
    localStorage.getItem("scannedTableId") ||
    "0";
  const isParcel = String(scannedTable).toUpperCase().includes("PARCEL");

  const incomingActiveTab = location.state?.activeTab || sessionStorage.getItem("customerActiveTab");
  const [activeTab, setActiveTab] = useState(() => (isParcel ? "Pick Up" : (incomingActiveTab || "Dine In")));

  const [cartDineIn, setCartDineIn] = useState(() => {
    try {
      const saved = sessionStorage.getItem("customerCartDineIn");
      if (saved) return JSON.parse(saved);
      if (!isParcel) {
        const legacy = sessionStorage.getItem("customerLandingCart");
        return legacy ? JSON.parse(legacy) : {};
      }
      return {};
    } catch {
      return {};
    }
  });

  const [cartPickUp, setCartPickUp] = useState(() => {
    try {
      const saved = sessionStorage.getItem("customerCartPickUp");
      if (saved) return JSON.parse(saved);
      if (isParcel) {
        const legacy = sessionStorage.getItem("customerLandingCart");
        return legacy ? JSON.parse(legacy) : {};
      }
      return {};
    } catch {
      return {};
    }
  });

  const [buttonState, setButtonState] = useState("hidden"); // "hidden" | "floating" | "docked"

  const activeCart = activeTab === "Pick Up" ? cartPickUp : cartDineIn;
  const setActiveCart = activeTab === "Pick Up" ? setCartPickUp : setCartDineIn;

  useEffect(() => {
    try {
      sessionStorage.setItem("customerCartDineIn", JSON.stringify(cartDineIn));
      sessionStorage.setItem("customerCartPickUp", JSON.stringify(cartPickUp));
      sessionStorage.setItem("customerActiveTab", activeTab);
      // Keep legacy customerLandingCart updated with all items
      sessionStorage.setItem("customerLandingCart", JSON.stringify({ ...cartDineIn, ...cartPickUp }));
    } catch {}
  }, [cartDineIn, cartPickUp, activeTab]);

  // Get workflowPlan from Redux (set when restaurant details load) or localStorage fallback
  const reduxRestaurantInfo = useSelector((state) => state.data?.restaurantInfo);
  const storeInfo = reduxRestaurantInfo || getStoreInfo();
  const isOrderFlow = storeInfo?.workflowPlan === "ORDER FLOW";

  useEffect(() => {
    const handleScroll = () => {
      if (!menuSectionRef.current) return;
      const rect = menuSectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // 1. Hide button when on Home / Hero banner (before menu section comes into view)
      if (rect.top > viewportHeight - 120) {
        setButtonState("hidden");
        return;
      }

      // 2. Dock button at the bottom of the menu section when reaching end of menu (above footer)
      if (rect.bottom <= viewportHeight + 10) {
        setButtonState("docked");
        return;
      }

      // 3. Float button when actively scrolling inside menu section
      setButtonState("floating");
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const matchCategory = (category) => {
    setSelectedCategory(category);
  };

  const filteredItems = selectedCategory
    ? menuItems.filter((item) => item.category === selectedCategory)
    : menuItems;

  useEffect(() => {
    document.body.classList.add("restaurant-scss");
  }, []);

  const updateLandingQuantity = (item, change) => {
    if (change > 0 && item.availability === false) {
      dispatch(
        showToast({
          message: "This item is currently out of stock.",
          type: "error",
        })
      );
      return;
    }

    const itemId = item._id;
    const prevItem = activeCart[itemId] || {
      quantity: 0,
      price: item.price,
      name: item.name,
      image: item.image,
    };
    const newQuantity = (prevItem.quantity || 0) + change;

    if (newQuantity <= 0) {
      const updated = { ...activeCart };
      delete updated[itemId];
      setActiveCart(updated);
      return;
    }

    setActiveCart({
      ...activeCart,
      [itemId]: {
        ...prevItem,
        quantity: newQuantity,
        price: item.price,
        name: item.name,
        image: item.image,
      },
    });
  };

  const dineInCount = useMemo(() => {
    return Object.values(cartDineIn || {}).reduce((s, it) => s + (it.quantity || 0), 0);
  }, [cartDineIn]);

  const pickUpCount = useMemo(() => {
    return Object.values(cartPickUp || {}).reduce((s, it) => s + (it.quantity || 0), 0);
  }, [cartPickUp]);

  const { totalItems, totalAmount } = useMemo(() => {
    const allItems = [...Object.values(cartDineIn || {}), ...Object.values(cartPickUp || {})];
    const count = allItems.reduce((sum, it) => sum + (it.quantity || 0), 0);
    const cost = allItems.reduce(
      (sum, it) => sum + (it.quantity || 0) * (it.price || 0),
      0
    );
    return { totalItems: count, totalAmount: cost };
  }, [cartDineIn, cartPickUp]);

  const handleOrderNow = () => {
    navigate(`/list/${scannedTable}`, {
      state: {
        cartDineIn,
        cartPickUp,
        selectedTab: activeTab,
        selectedCategory,
      },
    });
  };

  return (
    <section
      id="menus"
      ref={menuSectionRef}
      className={`position-relative ${!hasBanner ? "menu-no-banner" : ""}`}
    >
      {/* In-Section Static Backdrop: Pinned in viewport for the entire menu duration */}
      <div className="menu-backdrop-wrapper">
        <div 
          className="menu-static-backdrop"
          style={{
            backgroundImage: `url(${food_menu_bg})`,
          }}
        />
      </div>

      <div className="position-relative" style={{ zIndex: 1 }}>
        {/* Menu Header & Category Filter Bar */}
        <div className="menu-sticky-header">
          <div className="container-fluid px-3 px-md-4 px-xl-5">
            <div className="section-title text-center">
              <h5 className="title-black-50 h2 mb-0">Our Menu</h5>
              <h4 className="h4 text-black mb-2" style={{ marginTop: '4px' }}>Choose your mixture & order now!</h4>
            </div>

            <div className="row justify-content-center">
              <div className="col-12 filters-group-wrap text-center">
                <div className="filters-group">
                  <ul className="container-filter mb-0 categories-filter list-unstyled filter-options d-flex flex-wrap justify-content-center align-items-center">
                    {categories.map((category, index) => (
                      <li
                        key={index}
                        className={
                          selectedCategory === category ||
                          (category === "All" && selectedCategory === null)
                            ? "list-inline-item categories text-uppercase position-relative active"
                            : "list-inline-item categories text-uppercase position-relative"
                        }
                        data-group={category.toLowerCase().replace(/\s+/g, "-")}
                        onClick={() =>
                          matchCategory(category === "All" ? null : category)
                        }
                      >
                        {category}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Mode Switcher for Table Orders: Dine In vs Takeaway */}
            {!isParcel && isOrderFlow && (
              <div className="d-flex justify-content-center mt-3">
                <div
                  className="btn-group p-1 bg-white rounded-pill shadow-sm border"
                  role="group"
                  style={{ maxWidth: "340px", width: "100%" }}
                >
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill py-1 fw-bold ${
                      activeTab === "Dine In"
                        ? "btn-primary text-white shadow-sm"
                        : "btn-light text-muted"
                    }`}
                    style={{ fontSize: "13px", transition: "all 0.2s" }}
                    onClick={() => setActiveTab("Dine In")}
                  >
                    🍽️ Dine In ({dineInCount})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill py-1 fw-bold ${
                      activeTab === "Pick Up"
                        ? "btn-primary text-white shadow-sm"
                        : "btn-light text-muted"
                    }`}
                    style={{ fontSize: "13px", transition: "all 0.2s" }}
                    onClick={() => setActiveTab("Pick Up")}
                  >
                    🛍️ Takeaway ({pickUpCount})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="section pt-3 position-relative"
          style={{
            paddingBottom: isOrderFlow && menuItems.length > 0 ? "110px" : "60px",
          }}
        >
          <div className="container-fluid px-3 px-md-4 px-xl-5 w-100">
            <div
              className="row justify-content-center row-cols-xxl-6 row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-2 g-3 g-md-4"
              id="grid"
            >
            {filteredItems.map((item) => {
              const currentQty = activeCart[item._id]?.quantity || 0;
              const isOutOfStock = item.availability === false;

              return (
                <div className="col mt-4 picture-item" key={item._id}>
                  <div className="portfolio portfolio-primary menu-food h-100 d-flex flex-column align-items-center justify-content-between p-3">
                    <img
                      src={item.image}
                      className="item-image"
                      alt={item.name}
                    />

                    <div className="mt-3 text-center w-100">
                      <h5 className="title d-block mb-1">{item.name}</h5>
                      <span className="item-description d-block text-muted mb-1">
                        {item.description}
                      </span>
                      {/* Show badge for availability */}
                      {isOutOfStock && (
                        <div className="mb-2">
                          <span
                            className="badge d-inline-block"
                            style={{
                              background: "#fcd8d4",
                              color: "#f44336",
                              border: "1px dashed #f44336",
                              fontSize: "11px",
                              padding: "4px 8px",
                              borderRadius: "12px",
                            }}
                          >
                            Out of Stock
                          </span>
                        </div>
                      )}

                      {/* Integrated Price & Quantity Controls: [-] Rupees [+] */}
                      <div className="landing-price-qty-row">
                        {isOrderFlow && (
                          <button
                            type="button"
                            className={`qty-btn-inline ${
                              currentQty === 0 ? "qty-disabled" : ""
                            }`}
                            onClick={() => updateLandingQuantity(item, -1)}
                            disabled={currentQty === 0 || isOutOfStock}
                            title="Decrease quantity"
                          >
                            -
                          </button>
                        )}

                        <div className="price-display">
                          <span className="price-amount">₹{item.price}</span>
                          {isOrderFlow && currentQty > 0 && (
                            <span
                              className="badge bg-primary text-white rounded-pill px-2 py-0 ms-1"
                              style={{ fontSize: "11px" }}
                            >
                              {currentQty}
                            </span>
                          )}
                        </div>

                        {isOrderFlow && (
                          <button
                            type="button"
                            className="qty-btn-inline"
                            onClick={() => updateLandingQuantity(item, 1)}
                            disabled={isOutOfStock}
                            title="Add item"
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>

      {/* ORDER FLOW: Sticky Floating CTA Bar at Bottom */}
      {isOrderFlow && menuItems.length > 0 && buttonState !== "hidden" && (
        <div
          className={`landing-floating-bar-wrapper ${
            buttonState === "docked" ? "is-docked" : "is-floating"
          }`}
        >
          <button
            type="button"
            className="landing-floating-order-btn"
            onClick={handleOrderNow}
          >
            {totalItems > 0 ? (
              <span className="d-flex align-items-center justify-content-between w-100 gap-3">
                <span className="floating-cart-badge">
                  🛍️ {totalItems} item{totalItems > 1 ? "s" : ""}
                </span>
                <span className="floating-divider">|</span>
                <span className="floating-cart-total">₹{totalAmount}</span>
                <span className="floating-cta-text ms-2">
                  Review Order <span className="arrow-icon">→</span>
                </span>
              </span>
            ) : (
              <span className="d-flex align-items-center gap-2">
                <span>🛒</span>
                <span>Review Order</span>
              </span>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default Menus;

